import React, { createContext, useState, Dispatch, SetStateAction, useEffect } from 'react';
import {
  authListener,
  fetchUserProfile,
  logoutUser,
  signInUser,
  fetchUserEvents,
  updateUserVideoArray,
} from '../services/fetch-utils';
import { validateEmail } from '../services/verifyEmail';
import { AttendingEvent, EventDetails, UserProfile, Video } from '../interfaces/userInterfaces';

type UserProfileType = {
  user: UserProfile | null;
  token: string | null;
  userAttendingEventsList: number[];
  logout: () => void;
  isLoggedIn: () => boolean;
  isUserProfile: (value: UserProfile) => boolean;
  setUser: Dispatch<SetStateAction<UserProfile>>;
  loginUser: (email: string, password: string) => void;
  updateUserEventsListAfterAction: (user_id: string) => void;
  updateUserVideoListAfterAction: (user_id: string) => void;
  registerNewUser: (email: string, username: string, password: string) => void;
};

export const UserContext = createContext<UserProfileType>({} as UserProfileType);

export const UserProvider = ({ children }: { children: React.ReactNode }) => {
  //   Initialize Supabase Auth Listener Func
  authListener();

  const [token, setToken] = useState<string | null>(null);
  const [isReady, setIsReady] = useState(false);
  const [userAttendingEventsList, setUserAttendingEventsList] = useState<number[]>([]);

  const [user, setUser] = useState<UserProfile>({
    attending_event: [],
    user_events: [],
    avatar_url: '',
    created_at: '',
    email: '',
    id: 0,
    merch: [],
    songs: [],
    user_id: '',
    username: '',
    videos: [],
    wallet: { balance: 0 },
  });

  // Check localStorage for user object
  useEffect(() => {
    const user = localStorage.getItem('user');
    const token = localStorage.getItem('token');
    if (user && token) {
      setUser(JSON.parse(user));
      setToken(token);
    }
    setIsReady(true);
  }, []);

  const isLoggedIn = () => {
    if (localStorage.getItem('user')) {
      return true;
    } else {
      return false;
    }
  };

  const isUserProfile = (value: UserProfile): value is UserProfile => {
    if (value.user_id && value.user_id !== '') {
      return true;
    } else {
      return false;
    }
  };

  const isEventDetailsList = (value: EventDetails[]): value is EventDetails[] => {
    if (value[0].user_id && value[0].event_name) {
      return true;
    } else {
      return false;
    }
  };

  const isAttendingEventList = (value: AttendingEvent[]): value is AttendingEvent[] => {
    if (value[0].user_id && value[0].event_id) {
      return true;
    } else {
      return false;
    }
  };

  const isVideoMediaList = (value: Video[]): value is Video[] => {
    if (value[0]?.username && value[0]?.video_id) {
      return true;
    } else {
      return false;
    }
  };

  const registerNewUser = async (email: string, password: string, username: string) => {
    await signInUser(email, password).then((res) => {
      if (res) {
        console.log('RESPONSE FROM REGISTER NEW USER', res);
        localStorage.setItem('token', JSON.stringify(res?.data.session?.access_token));
        localStorage.setItem('user', JSON.stringify(res?.data.user));
        setToken(res?.data.session?.access_token as string);
      }
      if (res?.data) {
        const userID = res.data.user?.id as string;
        fetchUserProfile(userID).then((res) => setUser(res?.data));
      }
    });
  };

  const loginUser = async (email: string, password: string) => {
    if (validateEmail(email)) {
      const signInAttempt = await signInUser(email, password);

      if (signInAttempt?.data?.user !== null) {
        localStorage.setItem('token', JSON.stringify(signInAttempt?.data.session?.access_token));
        setToken(signInAttempt?.data.session?.access_token as string);
      } else {
        console.log('There was an Error During Login');
      }

      if (typeof signInAttempt?.data?.user?.id === 'string') {
        const userProfileData = await fetchUserProfile(signInAttempt.data.user.id);

        if (userProfileData.status === 200 && isUserProfile(userProfileData.data)) {
          setUser(userProfileData.data);
          localStorage.setItem('user', JSON.stringify(userProfileData.data));
        } else {
          console.log('FAILED TO FETCH PROFILE');
        }
      } else {
        console.log('User Data Not Present');
      }
    } else {
      throw new Error('EMAIL OR PASSWORD INVALID');
    }
  };

  const logout = () => {
    logoutUser();
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser({
      attending_event: [],
      user_events: [],
      avatar_url: '',
      created_at: '',
      email: '',
      id: 0,
      merch: [],
      songs: [],
      user_id: '',
      username: '',
      videos: [],
      wallet: { balance: 0 },
    });
    setToken('');
  };

  const updateUserEventsListAfterAction = async () => {
    const updatedEventList = await fetchUserEvents(user.user_id);
    console.log('UPDATED DATA FOR AFTER EVENT ACTION', updatedEventList);

    if (
      updatedEventList.status === 200 &&
      updatedEventList.data &&
      isEventDetailsList(updatedEventList.data.user_events) &&
      isAttendingEventList(updatedEventList.data.attending_event)
    ) {
      setUser({
        ...user,
        attending_event: updatedEventList.data.attending_event,
        user_events: updatedEventList.data.user_events,
      });
      console.log('Update Action Successfull');
    } else {
      console.log(
        'AN ERROR OCCURED IN UPDATE USER EVENTS AFTER ACTION FETCH CALL',
        updatedEventList
      );
    }
  };

  const updateUserVideoListAfterAction = async () => {
    const updatedMediaList = await updateUserVideoArray(user.user_id);
    if (
      updatedMediaList.status === 200 &&
      updatedMediaList.data &&
      isVideoMediaList(updatedMediaList.data.videos)
    ) {
      setUser({ ...user, videos: updatedMediaList.data.videos });
      localStorage.setItem(
        'user',
        JSON.stringify({ ...user, videos: updatedMediaList.data.videos })
      );
      console.log('Update Action Successfull');
    } else if (updatedMediaList.status === 200 && updatedMediaList.data?.videos.length === 0) {
      setUser({ ...user, videos: [] });
      localStorage.setItem('user', JSON.stringify({ ...user, videos: [] }));
    } else {
      console.log('AN ERROR OCCURRED IN UPDATE USER VIDEO ARRAY');
    }
  };

  useEffect(() => {
    const mappedIdArray = user.attending_event?.map((event) => {
      return event.event_id;
    });
    setUserAttendingEventsList(mappedIdArray);
  }, [user.attending_event]);

  // const userData = useMemo(() => ({ user, setUser }), [user]);

  // MEMOIZE USER STATE
  // MEMOIZE USER STATE
  // MEMOIZE USER STATE

  return (
    <UserContext.Provider
      value={{
        user,
        token,
        userAttendingEventsList,
        registerNewUser,
        loginUser,
        logout,
        isLoggedIn,
        isUserProfile,
        setUser,
        updateUserEventsListAfterAction,
        updateUserVideoListAfterAction,
      }}
    >
      {' '}
      {isReady ? children : null}{' '}
    </UserContext.Provider>
  );
};
